/**
 * Monthly check-in timing and answers (ACC-013, ACC-014, ACC-015, MSG-045). Pure, so the cron, the
 * token landing page and the tests share one reading of "five days before renewal".
 */

export const CHECK_IN_LEAD_DAYS = 5;

export const CHECK_IN_ANSWERS = ["no_changes", "new_labs", "health_change"] as const;

export type CheckInAnswer = (typeof CHECK_IN_ANSWERS)[number];

const ISO_DAY = /^(\d{4}-\d{2}-\d{2})/;

const DAY_MS = 24 * 60 * 60 * 1000;

const RENEWS_ON = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  timeZone: "UTC",
});

/** The calendar day an ISO timestamp or YYYY-MM-DD falls on, UTC. */
export function checkInDay(iso: string): string {
  const day = ISO_DAY.exec(iso)?.[1];

  if (day === undefined) throw new Error(`not an ISO date: ${iso}`);

  return day;
}

export function isCheckInAnswer(value: string): value is CheckInAnswer {
  return CHECK_IN_ANSWERS.some((answer) => answer === value);
}

/**
 * True only on the calendar day five days before `nextShipOn`. Day 4 and day 6 are misses on
 * purpose: a function that treated "within five days" as due would still pass a 5-day check.
 */
export function checkInDueOn(nextShipOn: string, today: string): boolean {
  const ship = Date.parse(`${checkInDay(nextShipOn)}T00:00:00Z`);
  const now = Date.parse(`${checkInDay(today)}T00:00:00Z`);

  if (!Number.isFinite(ship) || !Number.isFinite(now)) return false;

  return Math.round((ship - now) / DAY_MS) === CHECK_IN_LEAD_DAYS;
}

/** The renewal day as the email writes it, like "Tuesday, October 20". */
export function formatRenewsOn(nextShipOn: string): string {
  return RENEWS_ON.format(new Date(`${checkInDay(nextShipOn)}T00:00:00Z`));
}

/**
 * ACC-015: when the lab service is known, the check-in names its usual retest window. Unknown
 * uploads add nothing, so the email does not guess.
 */
export function checkInRetestNote(provider: string | null): string | null {
  if (provider === null || provider === "unknown") return null;

  if (provider === "rythm") return "Rythm usually retests monthly.";

  if (provider === "function") return "Function Health usually retests every 6 months.";

  return "Most labs retest every 3 to 6 months.";
}

/**
 * MSG-045: new labs and a health change open the account at the right page. "No changes" stays put.
 */
export function checkInNextPath(answer: CheckInAnswer): string | null {
  if (answer === "new_labs") return "/account/labs";

  if (answer === "health_change") return "/account/intake";

  return null;
}

/** ACC-014: a health-change answer holds the next order until a physician reviews it. */
export function checkInHoldsNextOrder(answer: CheckInAnswer | null): boolean {
  return answer === "health_change";
}
